// src/modules/walkInCase/pages/WalkInCaseList.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWalkInCases, deleteWalkInCase, restoreWalkInCase } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchAgents } from '../../agents/api';
import {
  Table, Button, Space, Typography, Spin, Tag, Badge, Switch, Tooltip,
  message, Row, Col, Grid, Card, theme as antdTheme, Dropdown,
} from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, FilterOutlined, MoreOutlined, UndoOutlined } from '@ant-design/icons';
import { useDeleteConfirm } from '../../../components/DeleteConfirm';
import {
  CaseSearch, CaseFilterModal, ActiveFilterTags, COMMON_SEARCH_FIELDS, EMPTY_FILTERS, countActiveFilters, filtersToParams,
} from '../../../components/CaseListFilters';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const STATUS_COLORS = {
  NEW: 'default', EVALUATED: 'blue', PAYMENT_SETTLED: 'purple', BILL_GENERATED: 'cyan',
  IN_INVENTORY: 'gold', DISPATCHED: 'orange', SETTLED: 'green', CLOSED: 'lime',
};
const STATUSES = Object.keys(STATUS_COLORS);

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'UPI', label: 'UPI' },
];

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function WalkInCaseList() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false); // admin: list soft-deleted cases

  // Column order management
  const defaultColumnOrder = ['caseId', 'customer', 'branch', 'grossWt', 'netWt', 'finalPayment', 'createdAt', 'status', 'actions'];
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('walkInCaseColumnOrder');
    return saved ? JSON.parse(saved) : defaultColumnOrder;
  });

  const navigate = useNavigate();
  const { token } = antdTheme.useToken();
  const screens = useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();
  const { canViewModule, canViewCreateModule, canEditModule, can, canViewField, role } = usePermissions();
  const { user } = useAuth();
  const isAdmin = role === 'admin';
  const { confirmDelete, confirmPermanentDelete, confirmRestore, contextHolder: deleteModalHolder } = useDeleteConfirm();

  // Detect HO user and load branch list for filter
  useEffect(() => {
    const init = async () => {
      try {
        if (isAdmin) {
          setIsHO(true);
        } else if (user?.branch) {
          const branchRes = await fetchBranchById(user.branch);
          const branch = branchRes?.data || branchRes;
          if (branch?.branchType === 'HO') setIsHO(true);
        }
        const res = await fetchBranches({ limit: 200 });
        const list = res.data || res;
        setBranches(Array.isArray(list) ? list.filter(b => b.branchType !== 'HO') : []);
      } catch (_) {}
      try {
        const res = await fetchAgents({ limit: 500 });
        const list = res.data || res;
        setAgents(Array.isArray(list) ? list : []);
      } catch (_) {}
    };
    init();
  }, [isAdmin, user?.branch]);

  // Field-level view permission checks for list columns
  const canViewCustomer = canViewField('walkInCase', 'customerId');
  const canViewStatusField = canViewField('walkInCase', 'status');
  const canViewGoldItems = canViewField('walkInCase', 'goldItems');
  const canViewTotalPurchase = canViewField('walkInCase', 'totalPurchaseAmount');

  // Handle column drag and drop
  const handleColumnDragStart = (e, columnKey) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('columnKey', columnKey);
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (e, targetColumnKey) => {
    e.preventDefault();
    const draggedColumnKey = e.dataTransfer.getData('columnKey');
    
    if (draggedColumnKey === targetColumnKey) return;
    
    const draggedIdx = columnOrder.indexOf(draggedColumnKey);
    const targetIdx = columnOrder.indexOf(targetColumnKey);
    
    const newOrder = [...columnOrder];
    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedColumnKey);
    
    setColumnOrder(newOrder);
    localStorage.setItem('walkInCaseColumnOrder', JSON.stringify(newOrder));
  };

  useEffect(() => { load(); }, [pagination.current, pagination.pageSize, searchQuery, searchField, filters, showDeleted]);

  const applyFilters = (next) => {
    setFilters(next);
    setPagination((p) => ({ ...p, current: 1 }));
    setFilterModalOpen(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchWalkInCases({
        search: searchQuery,
        searchField: searchQuery ? searchField : '',
        page: pagination.current,
        limit: pagination.pageSize,
        ...filtersToParams(filters),
        deleted: showDeleted || '',
      });
      setCases(res.data || []);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch (error) {
      messageApi.error('Failed to load walk-in cases');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, permanent) => {
    try {
      await deleteWalkInCase(id, { permanent });
      messageApi.success(permanent ? 'Case permanently deleted' : 'Case deleted');
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreWalkInCase(id);
      messageApi.success('Case restored');
      load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Restore failed');
    }
  };

  const columns = [
    {
      key: 'branch',
      title: 'Branch',
      render: (_, r) => r.branch?.name || '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'caseId',
      title: 'Case ID',
      dataIndex: 'caseId',
      render: (v, r) => <a onClick={() => navigate(`/walk-in-cases/${r._id}`)}>{v}</a>,
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'customer',
      title: 'Customer',
      render: (_, r) => r.customerId?.name ? (
        <>
          <div>{r.customerId.name}</div>
          {r.customerId.phone && <Text type="secondary" style={{ fontSize: 12 }}>{r.customerId.phone}</Text>}
        </>
      ) : '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'grossWt',
      title: 'Gross Wt. (gm)',
      dataIndex: 'totalGrossWeight',
      render: (val) => val ? `${val.toFixed(4)}` : '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'netWt',
      title: 'Net Wt. (gm)',
      dataIndex: 'totalNetWeight',
      render: (val) => val ? `${val.toFixed(4)}` : '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'finalPayment',
      title: 'Final Payment',
      dataIndex: 'finalPaymentToCustomer',
      render: (val) => fmt(val),
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      dataIndex: 'createdAt',
      render: (val) => val ? new Date(val).toLocaleDateString('en-IN') : '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      render: (v, r) => (
        <>
          <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>
          {r.isDeleted && (
            <Tooltip title={`Deleted${r.deletedBy?.name ? ` by ${r.deletedBy.name}` : ''}${r.deletedAt ? ` on ${new Date(r.deletedAt).toLocaleString('en-IN')}` : ''}`}>
              <Tag color="red">DELETED</Tag>
            </Tooltip>
          )}
        </>
      ),
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'actions',
      title: 'Action',
      width: 60,
      align: 'center',
      render: (_, r) => {
        const what = `case ${r.caseId}`;
        const actionItems = r.isDeleted ? [
          { key: 'view', label: 'View', icon: <EyeOutlined />, onClick: () => navigate(`/walk-in-cases/${r._id}`) },
          { key: 'restore', label: 'Restore', icon: <UndoOutlined />, onClick: () => confirmRestore({ what, onRestore: () => handleRestore(r._id) }) },
          {
            key: 'purge',
            label: 'Delete permanently',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => confirmPermanentDelete({ what, onDelete: () => handleDelete(r._id, true) }),
          },
        ] : [
          canViewModule('walkInCase') && {
            key: 'view',
            label: 'View',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/walk-in-cases/${r._id}`),
          },
          canEditModule('walkInCase') && {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            onClick: () => navigate(`/walk-in-cases/edit/${r._id}`),
          },
          can('walkInCase', 'delete') && (isAdmin || ['NEW', 'EVALUATED'].includes(r.status)) && {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            onClick: () => {
              // Past EVALUATED money has moved, so only a permanent (cascade) delete applies.
              if (!['NEW', 'EVALUATED'].includes(r.status)) {
                confirmPermanentDelete({ what: `${what} and all its linked records`, onDelete: () => handleDelete(r._id, true) });
              } else {
                confirmDelete({ what, onDelete: (permanent) => handleDelete(r._id, permanent) });
              }
            },
            danger: true,
          },
        ].filter(Boolean);

        return (
          <Dropdown menu={{ items: actionItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
      onHeaderCell: (column) => ({
        style: { cursor: 'default' },
      }),
    },
  ];

  // Sort columns by columnOrder
  const sortedColumns = columnOrder
    .map(key => columns.find(col => col.key === key))
    .filter(Boolean);

  if (!canViewModule('walkInCase')) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Title level={3}>Access Denied</Title>
            <Text>You don't have permission to view Walk-In Cases.</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: screens.md ? 24 : 12 }}>
      {contextHolder}
      {deleteModalHolder}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Walk-In Purchase Cases{showDeleted && <Text type="danger"> — Deleted</Text>}</Title>
        </Col>
        <Col>
          <Space>
            <CaseSearch
              fields={COMMON_SEARCH_FIELDS}
              field={searchField}
              onFieldChange={(f) => { setSearchField(f); setPagination((p) => ({ ...p, current: 1 })); }}
              onSearch={(v) => { setPagination((p) => ({ ...p, current: 1 })); setSearchQuery(v); }}
              size={screens.xs ? 'small' : 'middle'}
            />
            {isAdmin && (
              <Space size={4}>
                <Switch
                  size="small"
                  checked={showDeleted}
                  onChange={(v) => { setShowDeleted(v); setPagination((p) => ({ ...p, current: 1 })); }}
                />
                <Text type={showDeleted ? 'danger' : 'secondary'}>Show deleted</Text>
              </Space>
            )}
            <Badge count={countActiveFilters(filters)} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={() => setFilterModalOpen(true)}
                size={screens.xs ? 'small' : 'middle'}
              >
                {screens.xs ? '' : 'Filters'}
              </Button>
            </Badge>
            {canViewCreateModule('walkInCase') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/walk-in-cases/create')}
                size={screens.xs ? 'small' : 'middle'}
              >
                {screens.xs ? '' : 'New Case'}
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <CaseFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filters={filters}
        onApply={applyFilters}
        isHO={isHO}
        branches={branches}
        agents={agents}
        statusOptions={STATUSES}
        paymentModes={PAYMENT_MODES}
        amountLabel="Final Payment (₹)"
      />

      <ActiveFilterTags
        filters={filters}
        onChange={applyFilters}
        branches={branches}
        agents={agents}
        paymentModes={PAYMENT_MODES}
        fmtAmount={fmt}
      />

      <Spin spinning={loading}>
        <Table
          dataSource={cases}
          columns={sortedColumns}
          rowKey="_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (page, size) => setPagination({ current: page, pageSize: size, total: pagination.total }),
          }}
          scroll={{ x: 'max-content' }}
          size={screens.lg ? 'middle' : 'small'}
          style={{ background: token.colorBgContainer, borderRadius: token.borderRadius }}
        />
      </Spin>
    </div>
  );
}
